import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useState, type CSSProperties, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ApiError } from '@/api/client';
import type { PackageHandoff } from '@/features/storefront/account/pages/AccountPackagesPage';
import { CustomerAuthContext } from '@/features/storefront/account/customerAuthContext';
import { useShopContent } from '@/features/storefront/content/useContent';
import {
  useStorefrontAccountData,
  useStorefrontData,
  useStorefrontScope,
} from '@/features/storefront/data/context';
import { useStorefrontPaths } from '@/features/storefront/paths';
import {
  collectRazorpayPayment,
  isRazorpayPayload,
} from '@/features/storefront/payments/razorpay';
import { useStorefrontTheme } from '@/features/storefront/useStorefrontTheme';

/**
 * A pricing card's button for a plan that sells a session package.
 *
 * Packages belong to an account - the credits are spent from it when booking -
 * so a shopper who is not signed in is sent to sign in first and brought back
 * to this page (the same `state.from` the account guard uses). Signed in, the
 * purchase is started on the server, the gateway opens for its price, a "paid"
 * is checked with the server, and the shopper lands on their packages. A
 * gateway closed half-way leaves the purchase unpaid and says so here.
 */
export function PackageBuyButton({
  packageId,
  planName,
  className,
  style,
  children,
}: {
  packageId: string;
  planName: string;
  className: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const copy = useShopContent();
  const auth = useContext(CustomerAuthContext);
  const account = useStorefrontAccountData();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const paths = useStorefrontPaths();
  const theme = useStorefrontTheme();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const context = useQuery({
    queryKey: [...scope, 'context'],
    queryFn: () => source.context(),
    staleTime: 60_000,
  });

  async function buy() {
    if (busy || !account.buyPackage) return;
    setNote('');
    const customer = auth?.status === 'authenticated' ? auth.customer : null;
    if (!customer) {
      void navigate(paths.accountLogin(), {
        state: { from: `${location.pathname}${location.search}` },
      });
      return;
    }

    setBusy(true);
    try {
      const started = await account.buyPackage(packageId);
      const payment = started.payment;
      let outcome: 'paid' | 'awaiting' = 'paid';
      if (payment?.required) {
        if (!isRazorpayPayload(payment.provider_payload)) {
          outcome = 'awaiting';
        } else {
          let paymentId: string | null = null;
          outcome = await collectRazorpayPayment({
            payment: payment.provider_payload,
            amount: payment.amount,
            currency: payment.currency,
            storeName: context.data?.store_name ?? 'Store',
            orderNumber: started.purchase_id,
            description: planName,
            customer: { name: customer.name, email: customer.email, phone: customer.phone ?? undefined },
            themeColor: theme?.colors.primary,
            onPaymentId: (id) => {
              paymentId = id;
            },
          });
          if (outcome === 'paid' && paymentId && account.verifyPackagePurchase) {
            // A check that fails is not a failed payment: the webhook still lands.
            await account.verifyPackagePurchase(started.purchase_id, paymentId).catch(() => null);
          }
        }
      }
      if (outcome !== 'paid') {
        setNote(copy('plans.package_unpaid'));
        return;
      }
      void queryClient.invalidateQueries({ queryKey: [...scope, 'account', 'packages'] });
      const handoff: PackageHandoff = { packageOutcome: 'paid' };
      void navigate(paths.accountPackages(), { state: handoff });
    } catch (caught) {
      setNote(caught instanceof ApiError ? caught.message : copy('plans.package_error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => void buy()}
        disabled={busy}
        className={className}
        style={style}
        data-plan-package={packageId}
      >
        {busy ? copy('booking.paying') : children}
      </button>
      {note ? (
        <p className="mt-2 text-center text-xs" role="status" style={{ color: 'var(--storefront-muted)' }}>
          {note}
        </p>
      ) : null}
    </>
  );
}
